/* Chord box diagram; clicking it plays the chord. */
import { useState } from 'react';
import { audio } from '../core/audio';
import { type ChordRef, chordLongName, chordSymbol, parseChord, pretty, shapeFor } from '../core/theory';

export interface ChordDiagramProps {
  chord: string | ChordRef;
  caption?: string;
  /** false: static diagram; function: also called after the chord plays. */
  onClick?: false | ((name: string) => void);
}

export function ChordDiagram({ chord, caption, onClick }: ChordDiagramProps) {
  const [pulse, setPulse] = useState(false);
  const c = typeof chord === 'string' ? parseChord(chord) : chord;
  const name = typeof chord === 'string' ? chord : chordSymbol(chord.root, chord.q);
  const shape = c ? shapeFor(c.root, c.q, typeof chord === 'string' ? chord : null) : null;
  if (!c || !shape)
    return (
      <div className="chord-card">
        <div className="chord-name">{pretty(name)}</div>
      </div>
    );
  // room on the right for the barre's fret number ("10.")
  const W = 114;
  const H = 132;
  const x0 = 18;
  const y0 = 30;
  const sw = 14;
  const fh = 19;
  const fr = shape.frets;
  const fretted = fr.filter((f): f is number => f != null && f > 0);
  const maxF = fretted.length ? Math.max(...fretted) : 0;
  const minF = fretted.length ? Math.min(...fretted) : 0;
  const baseFret = maxF <= 4 ? 1 : minF;
  let barre = null;
  if (fretted.length >= 4) {
    const idxs = fr.map((f, i) => (f === minF ? i : -1)).filter((i) => i >= 0);
    if (idxs.length >= 2) {
      const a = idxs[0] as number;
      const b = idxs[idxs.length - 1] as number;
      let ok = true;
      for (let i = a; i <= b; i++) {
        const f = fr[i];
        if (f == null || f < minF) ok = false;
      }
      if (ok && b - a >= 2) {
        const y = y0 + (minF - baseFret + 0.5) * fh;
        barre = <rect x={x0 + a * sw - 6} y={y - 6} width={(b - a) * sw + 12} height={12} rx={6} className="cd-dot" />;
      }
    }
  }
  const svg = (
    <svg viewBox={`0 0 ${W} ${H}`} className="chord-svg" role="img" aria-label={`Akor şeması: ${pretty(name)}`}>
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <line key={`s${i}`} x1={x0 + i * sw} x2={x0 + i * sw} y1={y0} y2={y0 + fh * 5} className="cd-string" />
      ))}
      {[0, 1, 2, 3, 4, 5].map((j) => (
        <line
          key={`f${j}`}
          x1={x0}
          x2={x0 + 5 * sw}
          y1={y0 + j * fh}
          y2={y0 + j * fh}
          className={j === 0 && baseFret === 1 ? 'cd-nut' : 'cd-fret'}
        />
      ))}
      {baseFret > 1 ? (
        <text x={x0 + 5 * sw + 6} y={y0 + fh * 0.5 + 4} className="cd-base">
          {baseFret}.
        </text>
      ) : null}
      {barre}
      {fr.map((f, i) => {
        const x = x0 + i * sw;
        if (f == null)
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: one entry per string
            <text key={i} x={x} y={y0 - 7} className="cd-mute">
              ×
            </text>
          );
        // biome-ignore lint/suspicious/noArrayIndexKey: one entry per string
        if (f === 0) return <circle key={i} cx={x} cy={y0 - 10} r={3.6} className="cd-open" />;
        // biome-ignore lint/suspicious/noArrayIndexKey: one entry per string
        return <circle key={i} cx={x} cy={y0 + (f - baseFret + 0.5) * fh} r={5.4} className="cd-dot" />;
      })}
    </svg>
  );
  const clickable = onClick !== false;
  const play = async () => {
    audio.stopAll();
    await audio.resume();
    audio.playChord(name);
    setPulse(true);
    setTimeout(() => setPulse(false), 400);
    if (typeof onClick === 'function') onClick(name);
  };
  const title = chordLongName(c.root, c.q, /b/.test(name.slice(1, 2)) || undefined);
  const body = (
    <>
      <div className="chord-name">{pretty(name)}</div>
      {svg}
      {caption ? <div className="chord-cap">{caption}</div> : null}
    </>
  );
  if (!clickable)
    return (
      <div className="chord-card" title={title}>
        {body}
      </div>
    );
  return (
    // biome-ignore lint/a11y/useSemanticElements: card layout keeps the original div structure
    <div
      className={`chord-card clickable${pulse ? ' pulse' : ''}`}
      title={title}
      role="button"
      tabIndex={0}
      onClick={play}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          play();
        }
      }}
    >
      {body}
    </div>
  );
}
